import React, { useMemo, useState } from 'react';
import {
  CG_BOOST_NON_COMPARABLE_METRICS,
  CgBoostMetricKey,
  createCreativeGroup,
  CreativeGroupRow,
  CreativePayload,
  CreativeRow,
  dissolveCreativeGroup,
  getCreative,
  patchAsset,
  PLATFORM_LABELS,
} from '../../../services/cgBoostApi';
import { ViewDataBoundary } from '../useViewData';
import { cardStyle, flagToStatus, sectionTitle, tinyTable, tinyTd, tinyTh } from '../styles';
import { Pill, StatusPill } from '../../ui';
import { formatMetricValue } from '../../../config/metricTranslation';
import CreativeGroupSuggestionsPanel from './CreativeGroupSuggestionsPanel';
import InlineErrorBanner from '../InlineErrorBanner';

interface Props { campaignId: string; }

type SortKey = 'rank' | 'views' | 'hookRate' | 'vtr' | 'completionRate' | 'ctr' | 'resonance';
type ViewMode = 'asset' | 'grouped';

const CROSS_PLATFORM_TOOLTIP =
  'Group spans multiple platforms — TikTok, Meta, and Google Ads define this metric differently.';

function crossPlatformTooltip(
  metric: CgBoostMetricKey,
  crossPlatform: boolean,
): string | undefined {
  return crossPlatform && CG_BOOST_NON_COMPARABLE_METRICS.includes(metric)
    ? CROSS_PLATFORM_TOOLTIP
    : undefined;
}

const CreativeView: React.FC<Props> = ({ campaignId }) => (
  <ViewDataBoundary
    loader={getCreative}
    campaignId={campaignId}
    loadingLabel="Loading creative table…"
    render={(data, refetch) => (
      <CreativeTable campaignId={campaignId} data={data} onChanged={refetch} />
    )}
  />
);

const CreativeTable: React.FC<{
  campaignId: string;
  data: CreativePayload;
  onChanged?: () => void;
}> = ({ campaignId, data, onChanged }) => {
  const hasMultiMemberGroup = data.groups.some((g) => g.children.length > 1);
  const [viewMode, setViewMode] = useState<ViewMode>(hasMultiMemberGroup ? 'grouped' : 'asset');
  const [sortKey, setSortKey] = useState<SortKey>('rank');
  const [pendingAssetId, setPendingAssetId] = useState<string | null>(null);
  const [pendingGroupId, setPendingGroupId] = useState<string | null>(null);
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(new Set());
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(new Set());
  // Bumped on every successful change so the SuggestionsPanel re-pulls and
  // any newly-eligible (or just-dismissed) suggestions update without a
  // full route reload.
  const [suggestionsRefreshKey, setSuggestionsRefreshKey] = useState(0);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const triggerRefresh = (): void => {
    setSuggestionsRefreshKey((k) => k + 1);
    setMutationError(null);
    onChanged?.();
  };

  const sortedRows = useMemo(() => {
    const rows = [...data.rows];
    rows.sort((a, b) => {
      const aV = readRowSortValue(a, sortKey);
      const bV = readRowSortValue(b, sortKey);
      return sortKey === 'rank' ? aV - bV : bV - aV;
    });
    return rows;
  }, [data, sortKey]);

  const sortedGroups = useMemo(() => {
    const groups = [...data.groups];
    groups.sort((a, b) => {
      const aV = readGroupSortValue(a, sortKey);
      const bV = readGroupSortValue(b, sortKey);
      return sortKey === 'rank' ? aV - bV : bV - aV;
    });
    return groups;
  }, [data, sortKey]);

  const toggleAssetSelected = (id: string): void => {
    setSelectedAssetIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleExpand = (groupId: string): void => {
    setExpandedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const updateDiscoverySource = async (
    canonicalAssetId: string,
    discoverySource: 'confirmed' | 'hidden',
  ): Promise<void> => {
    setPendingAssetId(canonicalAssetId);
    try {
      await patchAsset(canonicalAssetId, { discoverySource });
      triggerRefresh();
    } catch (err: any) {
      console.warn('[CreativeView] patchAsset failed:', err);
      setMutationError(err?.message ?? 'Could not update curation. Please retry.');
    } finally {
      setPendingAssetId(null);
    }
  };

  const groupSelectedAssets = async (): Promise<void> => {
    const ids = Array.from(selectedAssetIds);
    if (ids.length < 2) return;
    setPendingGroupId('__creating__');
    try {
      await createCreativeGroup(campaignId, ids);
      setSelectedAssetIds(new Set());
      setViewMode('grouped');
      triggerRefresh();
    } catch (err: any) {
      console.warn('[CreativeView] createCreativeGroup failed:', err);
      setMutationError(err?.message ?? 'Could not create the group. Please retry.');
    } finally {
      setPendingGroupId(null);
    }
  };

  const ungroup = async (groupId: string): Promise<void> => {
    setPendingGroupId(groupId);
    try {
      await dissolveCreativeGroup(campaignId, groupId);
      setExpandedGroupIds((prev) => {
        const next = new Set(prev);
        next.delete(groupId);
        return next;
      });
      triggerRefresh();
    } catch (err: any) {
      console.warn('[CreativeView] dissolveCreativeGroup failed:', err);
      setMutationError(err?.message ?? 'Could not dissolve the group. Please retry.');
    } finally {
      setPendingGroupId(null);
    }
  };

  return (
    <div style={cardStyle}>
      <CreativeGroupSuggestionsPanel
        campaignId={campaignId}
        refreshKey={suggestionsRefreshKey}
        onChanged={triggerRefresh}
      />
      {mutationError && (
        <InlineErrorBanner
          message={mutationError}
          onDismiss={() => setMutationError(null)}
          style={{ marginBottom: 8 }}
        />
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <div style={sectionTitle}>Which assets worked and what should we brief next?</div>
          <div style={{ fontSize: 12, color: 'var(--fg-3)' }}>
            Ranked by Content Resonance Score (VTR + Watch Completion Rate) ÷ 2
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 4 }}>
            <Pill
              tone={viewMode === 'asset' ? 'accent' : 'neutral'}
              size="sm"
              active={viewMode === 'asset'}
              onClick={() => setViewMode('asset')}
            >
              Per asset
            </Pill>
            <Pill
              tone={viewMode === 'grouped' ? 'accent' : 'neutral'}
              size="sm"
              active={viewMode === 'grouped'}
              onClick={() => setViewMode('grouped')}
            >
              Grouped
            </Pill>
          </div>
          <div style={{ display: 'flex', gap: 6, fontSize: 11, color: 'var(--fg-3)' }}>
            <span>Sort:</span>
            {(['rank', 'views', 'hookRate', 'vtr', 'completionRate', 'ctr', 'resonance'] as SortKey[]).map((k) => (
              <button
                key={k}
                onClick={() => setSortKey(k)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: sortKey === k ? 'var(--accent)' : 'var(--fg-3)',
                  fontWeight: sortKey === k ? 600 : 400,
                }}
              >
                {sortLabel(k)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {viewMode === 'asset' && selectedAssetIds.size >= 2 && (
        <div style={selectionBar}>
          <span>
            <strong>{selectedAssetIds.size}</strong> assets selected — bind them as one creative
            group so the dashboard can roll up cross-platform performance.
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <Pill tone="success" size="sm" disabled={pendingGroupId !== null} onClick={groupSelectedAssets}>
              Group {selectedAssetIds.size} selected
            </Pill>
            <Pill tone="neutral" size="sm" onClick={() => setSelectedAssetIds(new Set())}>
              Cancel
            </Pill>
          </div>
        </div>
      )}

      {viewMode === 'asset' ? (
        <AssetTable
          rows={sortedRows}
          selectable={true}
          selectedIds={selectedAssetIds}
          onToggleSelect={toggleAssetSelected}
          pendingAssetId={pendingAssetId}
          onCurationChange={updateDiscoverySource}
        />
      ) : (
        <GroupedTable
          groups={sortedGroups}
          expandedIds={expandedGroupIds}
          onToggleExpand={toggleExpand}
          pendingAssetId={pendingAssetId}
          pendingGroupId={pendingGroupId}
          onCurationChange={updateDiscoverySource}
          onUngroup={ungroup}
        />
      )}
    </div>
  );
};

const AssetTable: React.FC<{
  rows: CreativeRow[];
  selectable: boolean;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  pendingAssetId: string | null;
  onCurationChange: (id: string, next: 'confirmed' | 'hidden') => void;
}> = ({ rows, selectable, selectedIds, onToggleSelect, pendingAssetId, onCurationChange }) => (
  <table style={tinyTable}>
    <thead>
      <tr>
        {selectable && <th style={{ ...tinyTh, width: 24 }}></th>}
        <th style={tinyTh}>#</th>
        <th style={tinyTh}>Creator</th>
        <th style={tinyTh}>Format</th>
        <th style={tinyTh}>Hook Style</th>
        <th style={tinyTh}>Platform</th>
        <th style={{ ...tinyTh, textAlign: 'right' }}>Views</th>
        <th style={{ ...tinyTh, textAlign: 'right' }}>Hook Rate</th>
        <th style={{ ...tinyTh, textAlign: 'right' }}>VTR</th>
        <th style={{ ...tinyTh, textAlign: 'right' }}>Completion</th>
        <th style={{ ...tinyTh, textAlign: 'right' }}>CTR</th>
        <th style={{ ...tinyTh, textAlign: 'right' }}>Resonance</th>
        <th style={tinyTh}>Flags</th>
        <th style={tinyTh}>Curation</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <AssetRow
          key={r.canonicalAssetId}
          row={r}
          selectable={selectable}
          selected={selectedIds.has(r.canonicalAssetId)}
          onToggleSelect={onToggleSelect}
          pendingAssetId={pendingAssetId}
          onCurationChange={onCurationChange}
        />
      ))}
    </tbody>
  </table>
);

const AssetRow: React.FC<{
  row: CreativeRow;
  selectable: boolean;
  selected: boolean;
  onToggleSelect: (id: string) => void;
  pendingAssetId: string | null;
  onCurationChange: (id: string, next: 'confirmed' | 'hidden') => void;
  indented?: boolean;
}> = ({ row: r, selectable, selected, onToggleSelect, pendingAssetId, onCurationChange, indented }) => {
  const isHidden = r.discoverySource === 'hidden';
  const isPending = pendingAssetId === r.canonicalAssetId;
  const rowStyle: React.CSSProperties = {
    ...(isHidden ? { opacity: 0.45 } : {}),
    ...(indented ? { background: 'var(--bg-hover)' } : {}),
  };
  const firstCellStyle: React.CSSProperties = indented
    ? { ...tinyTd, paddingLeft: 32, color: 'var(--fg-3)' }
    : tinyTd;
  return (
    <tr style={rowStyle}>
      {selectable && (
        <td style={tinyTd}>
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect(r.canonicalAssetId)}
            disabled={r.creativeGroupId !== undefined}
            title={
              r.creativeGroupId !== undefined
                ? 'Already grouped — switch to Grouped view to manage.'
                : 'Select to group across platforms'
            }
          />
        </td>
      )}
      <td style={firstCellStyle}>{r.rank}</td>
      <td style={{ ...tinyTd, fontWeight: 600 }}>{r.creatorName}</td>
      <td style={tinyTd}>{r.format}</td>
      <td style={{ ...tinyTd, color: 'var(--fg-3)' }}>{r.hookStyle}</td>
      <td style={tinyTd}>{PLATFORM_LABELS[r.platform]}</td>
      <td style={{ ...tinyTd, textAlign: 'right' }}>{formatMetricValue('views', r.metrics.views)}</td>
      <td style={{ ...tinyTd, textAlign: 'right' }}>{formatMetricValue('hookRate', r.metrics.hookRate)}</td>
      <td style={{ ...tinyTd, textAlign: 'right' }}>{formatMetricValue('vtr', r.metrics.vtr)}</td>
      <td style={{ ...tinyTd, textAlign: 'right' }}>{formatMetricValue('completionRate', r.metrics.completionRate)}</td>
      <td style={{ ...tinyTd, textAlign: 'right' }}>{formatMetricValue('ctr', r.metrics.ctr)}</td>
      <td style={{ ...tinyTd, textAlign: 'right', fontWeight: 600 }}>
        {(r.contentResonanceScore * 100).toFixed(1)}%
      </td>
      <td style={tinyTd}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {r.flags.topAsset && <StatusPill variant={flagToStatus.top} noDot size="sm">Top Asset</StatusPill>}
          {r.flags.hookStrength && <StatusPill variant={flagToStatus.info} noDot size="sm">Hook Strength</StatusPill>}
        </div>
      </td>
      <td style={tinyTd}>
        <CurationCell
          discoverySource={r.discoverySource}
          pending={isPending}
          onChange={(next) => onCurationChange(r.canonicalAssetId, next)}
        />
      </td>
    </tr>
  );
};

const GroupedTable: React.FC<{
  groups: CreativeGroupRow[];
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  pendingAssetId: string | null;
  pendingGroupId: string | null;
  onCurationChange: (id: string, next: 'confirmed' | 'hidden') => void;
  onUngroup: (groupId: string) => void;
}> = ({ groups, expandedIds, onToggleExpand, pendingAssetId, pendingGroupId, onCurationChange, onUngroup }) => (
  <table style={tinyTable}>
    <thead>
      <tr>
        <th style={{ ...tinyTh, width: 24 }}></th>
        <th style={tinyTh}>#</th>
        <th style={tinyTh}>Creator / Group</th>
        <th style={tinyTh}>Format</th>
        <th style={tinyTh}>Platforms</th>
        <th style={{ ...tinyTh, textAlign: 'right' }}>Views</th>
        <th style={{ ...tinyTh, textAlign: 'right' }}>Hook Rate</th>
        <th style={{ ...tinyTh, textAlign: 'right' }}>VTR</th>
        <th style={{ ...tinyTh, textAlign: 'right' }}>Completion</th>
        <th style={{ ...tinyTh, textAlign: 'right' }}>CTR</th>
        <th style={{ ...tinyTh, textAlign: 'right' }}>Resonance</th>
        <th style={tinyTh}>Group</th>
      </tr>
    </thead>
    <tbody>
      {groups.map((g) => (
        <GroupRowBlock
          key={g.creativeGroupId}
          group={g}
          expanded={expandedIds.has(g.creativeGroupId)}
          onToggleExpand={onToggleExpand}
          pendingAssetId={pendingAssetId}
          pendingGroupId={pendingGroupId}
          onCurationChange={onCurationChange}
          onUngroup={onUngroup}
        />
      ))}
    </tbody>
  </table>
);

const GroupRowBlock: React.FC<{
  group: CreativeGroupRow;
  expanded: boolean;
  onToggleExpand: (id: string) => void;
  pendingAssetId: string | null;
  pendingGroupId: string | null;
  onCurationChange: (id: string, next: 'confirmed' | 'hidden') => void;
  onUngroup: (groupId: string) => void;
}> = ({ group, expanded, onToggleExpand, pendingAssetId, pendingGroupId, onCurationChange, onUngroup }) => {
  const isMulti = group.children.length > 1;
  const ungrouping = pendingGroupId === group.creativeGroupId;
  const tip = (k: CgBoostMetricKey): string | undefined =>
    crossPlatformTooltip(k, group.crossPlatform);
  return (
    <>
      <tr style={{ borderTop: '1px solid var(--border-subtle)' }}>
        <td style={tinyTd}>
          {isMulti && (
            <button
              onClick={() => onToggleExpand(group.creativeGroupId)}
              style={chevronStyle}
              title={expanded ? 'Collapse group' : 'Expand to per-platform rows'}
            >
              {expanded ? '▾' : '▸'}
            </button>
          )}
        </td>
        <td style={{ ...tinyTd, fontWeight: 600 }}>{group.rank}</td>
        <td style={{ ...tinyTd, fontWeight: 600 }}>
          {group.label}
          {isMulti && (
            <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--fg-3)', fontWeight: 400 }}>
              ({group.children.length} platforms)
            </span>
          )}
        </td>
        <td style={tinyTd}>{group.format}</td>
        <td style={tinyTd}>{group.platforms.map((p) => PLATFORM_LABELS[p]).join(' · ')}</td>
        <td style={{ ...tinyTd, textAlign: 'right' }} title={tip('views')}>
          {formatMetricValue('views', group.metrics.views)}{tip('views') && ' *'}
        </td>
        <td style={{ ...tinyTd, textAlign: 'right' }} title={tip('hookRate')}>
          {formatMetricValue('hookRate', group.metrics.hookRate)}{tip('hookRate') && ' *'}
        </td>
        <td style={{ ...tinyTd, textAlign: 'right' }} title={tip('vtr')}>
          {formatMetricValue('vtr', group.metrics.vtr)}{tip('vtr') && ' *'}
        </td>
        <td style={{ ...tinyTd, textAlign: 'right' }} title={tip('completionRate')}>
          {formatMetricValue('completionRate', group.metrics.completionRate)}
          {tip('completionRate') && ' *'}
        </td>
        <td style={{ ...tinyTd, textAlign: 'right' }}>
          {formatMetricValue('ctr', group.metrics.ctr)}
        </td>
        <td style={{ ...tinyTd, textAlign: 'right', fontWeight: 600 }}>
          {(group.contentResonanceScore * 100).toFixed(1)}%
        </td>
        <td style={tinyTd}>
          {isMulti && (
            <Pill
              tone="danger"
              size="sm"
              disabled={ungrouping}
              title="Dissolve this group — assets return to per-platform rows."
              onClick={() => onUngroup(group.creativeGroupId)}
            >
              Ungroup
            </Pill>
          )}
        </td>
      </tr>
      {isMulti && expanded && group.children.map((child) => (
        <GroupChildRow
          key={child.canonicalAssetId}
          row={child}
          pendingAssetId={pendingAssetId}
          onCurationChange={onCurationChange}
        />
      ))}
    </>
  );
};

const GroupChildRow: React.FC<{
  row: CreativeRow;
  pendingAssetId: string | null;
  onCurationChange: (id: string, next: 'confirmed' | 'hidden') => void;
}> = ({ row: r, pendingAssetId, onCurationChange }) => {
  const isHidden = r.discoverySource === 'hidden';
  const isPending = pendingAssetId === r.canonicalAssetId;
  const rowStyle: React.CSSProperties = {
    ...(isHidden ? { opacity: 0.45 } : {}),
    background: 'var(--bg-hover)',
  };
  return (
    <tr style={rowStyle}>
      <td style={tinyTd}></td>
      <td style={{ ...tinyTd, paddingLeft: 24, color: 'var(--fg-3)' }}>↳</td>
      <td style={{ ...tinyTd, color: 'var(--fg-3)' }}>{PLATFORM_LABELS[r.platform]}</td>
      <td style={tinyTd}>{r.format}</td>
      <td style={{ ...tinyTd, color: 'var(--fg-3)' }}>—</td>
      <td style={{ ...tinyTd, textAlign: 'right' }}>{formatMetricValue('views', r.metrics.views)}</td>
      <td style={{ ...tinyTd, textAlign: 'right' }}>{formatMetricValue('hookRate', r.metrics.hookRate)}</td>
      <td style={{ ...tinyTd, textAlign: 'right' }}>{formatMetricValue('vtr', r.metrics.vtr)}</td>
      <td style={{ ...tinyTd, textAlign: 'right' }}>{formatMetricValue('completionRate', r.metrics.completionRate)}</td>
      <td style={{ ...tinyTd, textAlign: 'right' }}>{formatMetricValue('ctr', r.metrics.ctr)}</td>
      <td style={{ ...tinyTd, textAlign: 'right', fontWeight: 600 }}>
        {(r.contentResonanceScore * 100).toFixed(1)}%
      </td>
      <td style={tinyTd}>
        <CurationCell
          discoverySource={r.discoverySource}
          pending={isPending}
          onChange={(next) => onCurationChange(r.canonicalAssetId, next)}
        />
      </td>
    </tr>
  );
};

type CurationAction = { label: string; tone: React.ComponentProps<typeof Pill>['tone']; next: 'confirmed' | 'hidden'; title: string };

const CURATION_LAYOUTS: Record<
  CreativeRow['discoverySource'],
  { badge?: { label: string; tone: React.ComponentProps<typeof Pill>['tone'] }; actions: CurationAction[] }
> = {
  auto: {
    badge: { label: 'New', tone: 'warning' },
    actions: [
      { label: 'Confirm', tone: 'success', next: 'confirmed', title: 'Mark as reviewed; clears the New badge.' },
      { label: 'Hide', tone: 'danger', next: 'hidden', title: "Exclude this asset's metrics from every aggregation." },
    ],
  },
  confirmed: {
    actions: [
      { label: 'Hide', tone: 'danger', next: 'hidden', title: "Exclude this asset's metrics from every aggregation." },
    ],
  },
  hidden: {
    badge: { label: 'Hidden', tone: 'neutral' },
    actions: [
      { label: 'Unhide', tone: 'info', next: 'confirmed', title: "Re-include this asset's metrics in aggregations." },
    ],
  },
};

const CurationCell: React.FC<{
  discoverySource: CreativeRow['discoverySource'];
  pending: boolean;
  onChange: (next: 'confirmed' | 'hidden') => void;
}> = ({ discoverySource, pending, onChange }) => {
  const layout = CURATION_LAYOUTS[discoverySource];
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
      {layout.badge && (
        <Pill tone={layout.badge.tone} size="sm">{layout.badge.label}</Pill>
      )}
      {layout.actions.map((a) => (
        <Pill key={a.label} tone={a.tone} size="sm" disabled={pending} title={a.title} onClick={() => onChange(a.next)}>
          {a.label}
        </Pill>
      ))}
    </div>
  );
};

function readRowSortValue(r: CreativeRow, key: SortKey): number {
  switch (key) {
    case 'rank': return r.rank;
    case 'views': return r.metrics.views;
    case 'hookRate': return r.metrics.hookRate;
    case 'vtr': return r.metrics.vtr;
    case 'completionRate': return r.metrics.completionRate;
    case 'ctr': return r.metrics.ctr;
    case 'resonance': return r.contentResonanceScore;
  }
}

function readGroupSortValue(g: CreativeGroupRow, key: SortKey): number {
  switch (key) {
    case 'rank': return g.rank;
    case 'views': return g.metrics.views;
    case 'hookRate': return g.metrics.hookRate;
    case 'vtr': return g.metrics.vtr;
    case 'completionRate': return g.metrics.completionRate;
    case 'ctr': return g.metrics.ctr;
    case 'resonance': return g.contentResonanceScore;
  }
}

function sortLabel(k: SortKey): string {
  return ({
    rank: 'Rank',
    views: 'Views',
    hookRate: 'Hook',
    vtr: 'VTR',
    completionRate: 'Completion',
    ctr: 'CTR',
    resonance: 'Resonance',
  } as const)[k];
}

const selectionBar: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  padding: '8px 12px',
  marginBottom: 8,
  background: 'var(--accent-soft)',
  border: '1px solid var(--accent)',
  borderRadius: 6,
  fontSize: 12,
};

const chevronStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: 12,
  color: 'var(--fg-3)',
  padding: 2,
};

export default CreativeView;
